/**
 * Template Approval Workflow Hook
 *
 * FERP CRITICAL: Life-critical dual-control approval for workflow templates
 * Domain-agnostic, data-driven approval orchestration
 *
 * Principles:
 * - All approval policies loaded from backend (ApprovalPolicyRepository)
 * - Dual-control required for HIPAA templates (2 independent approvers)
 * - Complete audit trail with immutable evidence chain
 * - Repository pattern strict enforcement (no direct API calls)
 * - 16 primitive types only (ApprovalPolicy, ApprovalInstance primitives)
 */

import { useState, useCallback } from 'react';
import { useCurrentTenant } from './useCurrentTenant';
import { useAuth } from '../security/useAuth';
import { notifications } from '@mantine/notifications';
import { approvalRepository } from '../data/repositories';

// ============================================================
// TypeScript Types (Re-exported from Repository)
// ============================================================
// FERP PRINCIPLE: Types defined in repository layer, not duplicated in hooks

import type {
  ApprovalWorkflowInstance,
  ApprovalDecision,
  ApproverSpec,
  RecordDecisionInput,
  RevisionItem
} from '../data/repositories/ApprovalRepository';

// FERP PRINCIPLE: All enums from backend (imported as values for runtime use)
import {
  ApprovalStatus,
  DecisionType
} from '../data/repositories/ApprovalRepository';

// Re-export enums for consumers
export { ApprovalStatus, DecisionType };

// Convenience aliases for backward compatibility
export type ApprovalInstance = ApprovalWorkflowInstance;
export type RequiredApprover = ApproverSpec;
export type { ApprovalDecision };
export type ApprovalEvidence = {
  checklist_items_verified: string[];
  certifications_validated: string[];
  compliance_frameworks_checked: string[];
};
export type RecordApprovalDecisionInput = {
  instanceId: string;
  approverId: string;
  approverName: string;
  approverRole: string;
  decisionType: DecisionType; // From backend enum, not hardcoded
  comments?: string;
  revisionItems?: RevisionItem[];
  evidence?: ApprovalEvidence;
};
export type SubmitTemplateForApprovalInput = {
  template_id: string;
  template_version: string;
  tenant: string;
  submitted_by: string;
  metadata?: Record<string, unknown>;
};

// ============================================================
// Hook Implementation
// ============================================================

export function useTemplateApproval() {
  const { tenantId } = useCurrentTenant();
  const { user } = useAuth();

  const [currentApproval, setCurrentApproval] = useState<ApprovalInstance | null>(null);
  const [pendingApprovals, setPendingApprovals] = useState<ApprovalInstance[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Submit template for approval
   * REPOSITORY PATTERN: Uses approvalRepository.submitForApproval()
   * Triggers approval orchestrator to determine criticality and required approvers
   */
  const submitForApproval = useCallback(async (
    templateId: string,
    templateVersion: string,
    metadata?: Record<string, unknown>
  ): Promise<ApprovalInstance> => {
    setIsLoading(true);
    setError(null);

    try {
      const submitterId = user?.id || 'unknown';
      const approval = await approvalRepository.submitForApproval(
        templateId,
        templateVersion,
        submitterId
      );
      setCurrentApproval(approval);

      notifications.show({
        title: 'Approval Submitted',
        message: `Template ${templateId} v${templateVersion} submitted for ${approval.criticality} approval`,
        color: 'blue'
      });

      return approval;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to submit for approval');
      setError(error);

      notifications.show({
        title: 'Submission Failed',
        message: error.message,
        color: 'red'
      });

      throw error;

    } finally {
      setIsLoading(false);
    }
  }, [user]);

  /**
   * Record approval decision (approve/reject/conditional)
   * REPOSITORY PATTERN: Uses approvalRepository.recordDecision()
   * CRITICAL: Implements dual-control for life-critical templates
   */
  const recordDecision = useCallback(async (
    instanceId: string,
    decisionType: DecisionType,
    comments: string,
    revisionItems?: RevisionItem[]
  ): Promise<ApprovalWorkflowInstance> => {
    setIsLoading(true);
    setError(null);

    try {
      const input: RecordDecisionInput = {
        instanceId,
        approverId: user?.id || 'unknown',
        approverName: user?.name || 'Unknown User',
        approverRole: user?.roles?.[0] || 'Unknown Role', // FERP: User roles from auth system
        decisionType,
        comments,
        revisionItems
      };

      const updatedInstance = await approvalRepository.recordDecision(input);

      notifications.show({
        title: 'Decision Recorded',
        message: `Approval decision: ${decisionType}`,
        color: decisionType === DecisionType.APPROVED ? 'green' :
               decisionType === DecisionType.REJECTED ? 'red' : 'orange'
      });

      // Refresh approval status after decision
      await getApprovalStatus(instanceId);

      return updatedInstance;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to record decision');
      setError(error);

      notifications.show({
        title: 'Decision Failed',
        message: error.message,
        color: 'red'
      });

      throw error;

    } finally {
      setIsLoading(false);
    }
  }, [user]);

  /**
   * Get approval status for a specific instance
   * REPOSITORY PATTERN: Uses approvalRepository.getInstance()
   */
  const getApprovalStatus = useCallback(async (
    instanceId: string
  ): Promise<ApprovalInstance> => {
    setIsLoading(true);
    setError(null);

    try {
      const approval = await approvalRepository.getInstance(instanceId);

      if (!approval) {
        throw new Error(`Approval instance not found: ${instanceId}`);
      }

      setCurrentApproval(approval);
      return approval;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to get approval status');
      setError(error);
      throw error;

    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * List all pending approvals for current user
   * REPOSITORY PATTERN: Uses approvalRepository.listPendingApprovals()
   */
  const listPendingApprovals = useCallback(async (): Promise<ApprovalInstance[]> => {
    setIsLoading(true);
    setError(null);

    try {
      // Guard against null tenantId
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      const approvals = await approvalRepository.listPendingApprovals(
        tenantId,
        user?.id || ''
      );

      setPendingApprovals(approvals);
      return approvals;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to list pending approvals');
      setError(error);
      throw error;

    } finally {
      setIsLoading(false);
    }
  }, [tenantId, user]);

  return {
    // State
    currentApproval,
    pendingApprovals,
    isLoading,
    error,

    // Actions
    submitForApproval,
    recordDecision,
    getApprovalStatus,
    listPendingApprovals
  };
}

/**
 * Specialized hook for dual-control approval validation
 * CRITICAL: Used for HIPAA-compliant templates requiring 2 independent approvers
 * FERP PRINCIPLE: All field names from backend types (camelCase), calculations data-driven
 */
export function useDualControlApproval(approvalInstance: ApprovalInstance | null) {
  const isDualControlRequired = approvalInstance?.dualControlRequired ?? false;

  // Calculate counts from decisions array (data-driven)
  const approvalCount = approvalInstance?.decisions?.filter(
    d => d.decisionType === DecisionType.APPROVED
  ).length ?? 0;
  const rejectionCount = approvalInstance?.decisions?.filter(
    d => d.decisionType === DecisionType.REJECTED
  ).length ?? 0;
  const pendingCount = approvalInstance?.requiredApprovers?.length ?? 0 -
                       (approvalCount + rejectionCount);

  const isDualControlSatisfied = isDualControlRequired ? approvalCount >= 2 : approvalCount >= 1;
  const canPublish = isDualControlSatisfied && rejectionCount === 0;
  const isRejected = rejectionCount > 0;
  const isPending = approvalInstance?.status === ApprovalStatus.PENDING_REVIEW ||
                    approvalInstance?.status === ApprovalStatus.UNDER_REVIEW;

  return {
    isDualControlRequired,
    isDualControlSatisfied,
    canPublish,
    isRejected,
    isPending,
    approvalCount,
    rejectionCount,
    pendingCount
  };
}
